/**
 * Social Circle API Service
 * Connects to the same backend as mango-chat/mango-personal/mango-calendar.
 */
import { TOKEN_KEY } from '../constants';
import { createChatApi } from 'shared-ui';

const LIVE_API_URL = 'https://api.mango-business.solutions/api/v1';
const DEV_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

const API_BASE_URL = ((): string => {
  if (typeof window === 'undefined') return LIVE_API_URL;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return DEV_API_URL;
  return process.env.REACT_APP_API_URL || LIVE_API_URL;
})();

// mango-personal's own origin, for the "View in My Calendar"/"payment methods" deep links
// GroupThread renders - same discrepancy noted in mango-chat's api.ts (confirm the actual live
// origin before relying on the production fallback below).
const DEV_MANGO_PERSONAL_URL = process.env.REACT_APP_MANGO_PERSONAL_URL || 'http://localhost:3002';
const LIVE_MANGO_PERSONAL_URL = process.env.REACT_APP_MANGO_PERSONAL_URL || 'https://personal.mango-business.solutions';
export const MANGO_PERSONAL_URL = ((): string => {
  if (typeof window === 'undefined') return LIVE_MANGO_PERSONAL_URL;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return DEV_MANGO_PERSONAL_URL;
  return LIVE_MANGO_PERSONAL_URL;
})();

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

// Shared group-chat client (see shared/ui/chat/api.ts) - the same one mango-chat's GroupDetail
// wraps, so the group list + GroupThread pane in Circle.tsx behave identically to mango-chat.
export const chatApi = createChatApi({
  baseUrl: API_BASE_URL,
  getAuthHeaders,
  getToken: () => localStorage.getItem(TOKEN_KEY),
});

// Unified calendar - personal events plus pending (unaccepted) chat/group items - see backend
// personal-calendar-items.service.ts. Same endpoint mango-calendar/mango-chat/mango-personal use.
export const calendarApi = {
  getEvents: async (params?: { startDate?: string; endDate?: string }) => {
    const q = new URLSearchParams();
    if (params?.startDate) q.set('startDate', params.startDate);
    if (params?.endDate) q.set('endDate', params.endDate);
    const response = await fetch(`${API_BASE_URL}/personal-calendar/events${q.toString() ? '?' + q : ''}`, { headers: getAuthHeaders() });
    return response.json();
  },
  createEvent: async (data: { title: string; description?: string; startTime: string; endTime?: string }) => {
    const response = await fetch(`${API_BASE_URL}/personal-calendar/events`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    return response.json();
  },
  deleteEvent: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/personal-calendar/events/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    return response.json();
  },
};

export const authApi = {
  login: async (email: string, password: string) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    return response.json();
  },
  me: async () => {
    const response = await fetch(`${API_BASE_URL}/auth/me`, { headers: getAuthHeaders() });
    return response.json();
  },
};
